import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { Link, useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { ConnectLineAdsGroupModal } from "@/pages/applications/pages/lineAdsIntegration/ConnectLineAdsGroupModal";
import { DisconnectLineAdsModal } from "@/pages/applications/pages/lineAdsIntegration/DisconnectLineAdsModal";
import { isNotFoundError } from "@/pages/applications/pages/lineAdsIntegration/isNotFoundError";
import { LineAdsLoadError } from "@/pages/applications/pages/lineAdsIntegration/LineAdsLoadError";
import { ListLineAdsAccount } from "@/pages/applications/pages/lineAdsIntegration/ListLineAdsAccount";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const styles = {
  link: {
    textDecoration: "none",
  },
  connectSection: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxWidth: "512px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const LineAdsIntegrationPage: React.FC =
  function LineAdsIntegrationPage() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [isConnectOpen, toggleConnect] = useSwitch(false);
    const [isDisconnectOpen, toggleDisconnect] = useSwitch(false);
    // Database read only; the account list is where access is checked against LINE.
    const groupQuery = interlude.lineAds.useGetGroup({
      queries: { checkAccess: false },
    });

    const handleConnected = useHandler(() => {
      toggleConnect.off();
      enqueueSnackbar(
        t("applications.lineAds.connectModal.success", "LINE Ads connected"),
        { variant: "success" },
      );
    });
    const disconnectMutation = interlude.lineAds.useDisconnectGroup(undefined, {
      onSuccess() {
        enqueueSnackbar(
          t(
            "applications.lineAds.disconnectModal.success",
            "LINE Ads disconnected",
          ),
          { variant: "success" },
        );
        navigate({ to: "/applications" });
      },
      onError: toggleDisconnect.off,
    });
    const handleConfirmDisconnect = useHandler(() => {
      disconnectMutation.mutate(undefined);
    });

    const title = t("applications.lineAds.title", "LINE Ads");
    return (
      <PageLayout>
        <Stack gap="8px">
          <Breadcrumbs aria-label="breadcrumb" separator=">">
            <Box component={Link} to="/applications" sx={styles.link}>
              <Typography variant="body" color={theme.colors.staticFgBody}>
                {t("applications.title")}
              </Typography>
            </Box>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {title}
            </Typography>
          </Breadcrumbs>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="16px"
          >
            <PageTitle>{title}</PageTitle>
            {groupQuery.isSuccess ? (
              <Button variant="secondary" onClick={toggleDisconnect.on}>
                {t(
                  "applications.lineAds.disconnectModal.button",
                  "Disconnect LINE Ads",
                )}
              </Button>
            ) : null}
          </Stack>
          {groupQuery.isPending ||
          (groupQuery.isError && groupQuery.isFetching) ? (
            <BarLoading />
          ) : groupQuery.isSuccess ? (
            <ListLineAdsAccount />
          ) : !isNotFoundError(groupQuery.error) ? (
            <LineAdsLoadError error={groupQuery.error} />
          ) : (
            <Box sx={styles.connectSection}>
              <Typography
                variant="h3"
                fontWeight="medium"
                color={theme.colors.staticFgTitle}
              >
                {t("applications.lineAds.group.title", "LINE Ads group")}
              </Typography>
              <Typography variant="note" color={theme.colors.staticFgNote}>
                {t(
                  "applications.lineAds.group.description",
                  "Connect a LINE Ads group with its group ID, API access key and API secret key.",
                )}
              </Typography>
              <Button variant="primary" onClick={toggleConnect.on}>
                {t("common.connect")}
              </Button>
            </Box>
          )}
        </Stack>
        {isConnectOpen ? (
          <ConnectLineAdsGroupModal
            open
            onClose={toggleConnect.off}
            onConnected={handleConnected}
          />
        ) : null}
        {isDisconnectOpen ? (
          <DisconnectLineAdsModal
            open
            isLoading={disconnectMutation.isPending}
            onClose={toggleDisconnect.off}
            onConfirm={handleConfirmDisconnect}
          />
        ) : null}
      </PageLayout>
    );
  };
